import { withNamespaces } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Button,
	Spinner,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown,
} from "reactstrap";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
	errorNotification,
	successNotification,
} from "src/store/actions";
import QuestionnaireDesignerService from 'src/modules/data-protection/apis/QuestionnaireDesignerService';
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import ProcessingActivityService from "src/modules/data-protection/apis/ProcessingActivityService";
import { connect } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
    helpers
} from '@smartintegrity/questionnaire';
import CreationModal from "./components/CreationModal";
import EditModal from "./components/EditModal";

import ProcessingQuestionnaire from "./components/ProcessingQuestionnaire";
import AssessmentPanel from "../components/AssessmentPanel";
import AssessmentSummaryOffcanvas from "../components/summary-offcanvas";
import useComplianceModule, {
    COMPLIANCE_MODULE_SLUGS
} from "../hooks/useComplianceModule";
import { mapProcessingActivitySummary } from "../helpers/summaryCards";

import { 
    mapQuestions, 
    mapAnswers
} from "src/modules/data-protection/helpers/questionnaire";
import { buildComplianceObligationsMap } from "src/modules/data-protection/helpers/complianceObligations";
import {
    findReportSelectionCaseId,
    hasAssessmentQuestionFocus,
    readAssessmentQuestionFocusParams,
    reportCaseIdsEqual,
    stripAssessmentQuestionFocusFromSearch,
} from "../helpers/assessmentQuestionFocus";
import {
    getOverallPercent,
    getQuestionnaireProgress,
    getQuestionnaireQuestions
} from "../helpers/questionnaireProgress";

/**
 * Progress shown before the questionnaire itself reports its counters.
 */
const getReportProgress = (report) => {
    return getQuestionnaireProgress(report?.questions, report?.mappedAnswers);
};

const getReportGapCount = (report, obligationsByCode) => {
    return getQuestionnaireQuestions(report?.questions).reduce((count, question) => {
        const code = question?.systemData ?? question?.system_data;
        const obligations = code ? obligationsByCode[code] || [] : [];
        const gaps = obligations.filter((obligation) => {
            return obligation?.status == null || obligation.status === "gap";
        });

        return count + gaps.length;
    }, 0);
};

const ProcessingActivity = ({
	t,
	organization,
    softwareData,
    onProgressChange,
    onAssessmentSaved
}) => {
	const { softwareId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const focusParams = useMemo(() => {
        return readAssessmentQuestionFocusParams(location.search);
    }, [location.search]);
	const [ activityToDelete, setActivityToDelete ] = useState();
    const [ activityToEdit, setActivityToEdit ] = useState();
	const [ showCreationModal, setShowCreationModal ] = useState(false);
    const [selectedActivityId, setSelectedActivityId] = useState(null);
    const [activityProgress, setActivityProgress] = useState({});
    const [activeSummaryCard, setActiveSummaryCard] = useState(null);

	const pageEndRef = useRef();
    const appliedInitialFocusRef = useRef(false);

	const toggleActivityDeletionModal = () => setActivityToDelete(!activityToDelete);
	const toggleShowCreationModal = () => setShowCreationModal(!showCreationModal);
    const toggleActivityEditModal = () => setActivityToEdit(null);

    const processingQuestionsQuery = useQuery({
		queryKey: [
			"data-protection-softwares-questions-list",
            "processing_activity",
			softwareId
		],
		queryFn: async () => {
			const service = QuestionnaireDesignerService.getInstance();

			return await service.questions(
				"processing_activity",
				organization.id,
				{
					softwareId: softwareId
				}
			);
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: () => {
			if (process.env.NODE_ENV === 'development');

			toast(t("An error occurred while fetching questions."), {
				type: "error"
			});
		},
	});

	const {
		data: filledQuestionnaires,
		refetch: refetchActivities,
        isFetching: filledQuestionnairesAreFetching
	} = useQuery({
		queryKey: [
			"data-protection-fetch-software-processing-activities",
			softwareId,
            processingQuestionsQuery.dataUpdatedAt
		],
		queryFn: async () => {
			const service = DataProtectionSoftwareService.getInstance();

			const response =  await service.filledQuestionnaires(
				softwareId,
				"processing_activity"
			);

            return (response.reports || []).map((report) => {
                const mappedAnswers = mapAnswers(report.question_answers || []);

                const questions = mapQuestions(processingQuestionsQuery.data || [], mappedAnswers);

                return {
                    ...report,
                    questions: helpers.makeQuestionsReadyForQuestionnaire(
                        questions,
                        false
                    ),
                    question_answers: mappedAnswers,
                    mappedAnswers: helpers.makePreviousAnswersReadyForQuestionnaire(
                        questions,
                        mappedAnswers
                    )
                }
            })
		},
        enabled: processingQuestionsQuery.isFetched,
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: (error) => {
			if (process.env.NODE_ENV === 'development') console.error(error);

			toast(t("An error occurred while fetching processing activities."), {
				type: "error"
			});
		}
	});

    useEffect(() => {
        appliedInitialFocusRef.current = false;
    }, [location.search]);

    useEffect(() => {
        if (!filledQuestionnaires?.length) {
            setSelectedActivityId(null);
            return;
        }

        if (
            !appliedInitialFocusRef.current
            && hasAssessmentQuestionFocus(focusParams)
        ) {
            const focusedCaseId = findReportSelectionCaseId(
                filledQuestionnaires,
                focusParams
            );

            if (focusedCaseId != null) {
                appliedInitialFocusRef.current = true;
                setSelectedActivityId(focusedCaseId);
                return;
            }
        }

        setSelectedActivityId((current) => {
            if (
                current != null
                && filledQuestionnaires.some((report) => {
                    return reportCaseIdsEqual(report.case_id, current);
                })
            ) {
                return current;
            }

            return filledQuestionnaires[0].case_id;
        });
    }, [filledQuestionnaires, focusParams]);

    const focusedReportCaseId = useMemo(() => {
        if (!hasAssessmentQuestionFocus(focusParams)) {
            return null;
        }

        return findReportSelectionCaseId(
            filledQuestionnaires || [],
            focusParams
        );
    }, [filledQuestionnaires, focusParams]);

    const handleSelectActivity = useCallback((caseId) => {
        setSelectedActivityId(caseId);

        if (!hasAssessmentQuestionFocus(focusParams)) {
            return;
        }

        if (reportCaseIdsEqual(caseId, focusedReportCaseId)) {
            return;
        }

        history.replace({
            search: stripAssessmentQuestionFocusFromSearch(location.search),
        });
    }, [
        focusParams,
        focusedReportCaseId,
        history,
        location.search,
    ]);

	// Asset details are owned by the details page and passed in, so this tab
	// never re-requests them.
	const softwareDetails = softwareData;

    const {
        data: complianceModule,
        isError: summaryIsError,
        isFetching: summaryIsFetching,
        refetch: refetchSummary
    } = useComplianceModule(
        softwareId,
        COMPLIANCE_MODULE_SLUGS.processingActivity,
        { t }
    );
    const processingSummary = useMemo(() => {
        return mapProcessingActivitySummary(
            summaryIsError ? null : complianceModule?.summary
        );
    }, [complianceModule, summaryIsError]);

    const handleActivitySaved = useCallback(() => {
        refetchActivities();
        refetchSummary();

        onAssessmentSaved && onAssessmentSaved();
    }, [onAssessmentSaved, refetchActivities, refetchSummary]);

	const {
		mutate: deleteActivity,
		isLoading: activityDeletionIsLoading
	} = useMutation({
		mutationFn: async ({
			caseId
		}) => {
			const service = ProcessingActivityService.getInstance();
			return await service.deleteActivity({
				report_case_id: caseId
			})
		},
		onSuccess() {
			toast(t("Activity is removed"), {
				type: 'success'
			});
            handleActivitySaved();
			setActivityToDelete(null);
		},
		onError(error) {
			if (process.env.NODE_ENV === 'development') console.error(error)
			toast(t('Faild to remove the activity'), {
				type: "error"
			})
		}
	});

    const complianceObligations = useMemo(() => {
        return softwareData?.ai_act_obligations || [];
    }, [softwareData?.ai_act_obligations]);

    const obligationsByCode = useMemo(() => {
        return buildComplianceObligationsMap(complianceObligations);
    }, [complianceObligations]);

    const selectedReport = (filledQuestionnaires || []).find((report) => {
        return reportCaseIdsEqual(report.case_id, selectedActivityId);
    });

    const questionFocusForSelectedReport = useMemo(() => {
        if (
            !hasAssessmentQuestionFocus(focusParams)
            || focusedReportCaseId == null
            || !reportCaseIdsEqual(selectedActivityId, focusedReportCaseId)
        ) {
            return null;
        }

        return focusParams;
    }, [focusParams, focusedReportCaseId, selectedActivityId]);

    const handleProgressChange = useCallback((caseId, progress) => {
        setActivityProgress((current) => ({
            ...current,
            [caseId]: progress
        }));
    }, []);

    const overallProgress = useMemo(() => {
        return getOverallPercent(
            (filledQuestionnaires || []).map((report) => {
                return (
                    activityProgress[report.case_id] ||
                    getReportProgress(report)
                );
            })
        );
    }, [activityProgress, filledQuestionnaires]);

    useEffect(() => {
        onProgressChange && onProgressChange(overallProgress);
    }, [onProgressChange, overallProgress]);

	return (
		<>
            <AssessmentPanel
                id="processing-activity-assessment"
                title={t("Processing Activity")}
                subtitle={t(
                    "Understand how personal data is processed for this software, including purposes, legal basis, data categories and transfers."
                )}
                status={processingSummary.status ? {
                    ...processingSummary.status,
                    label: t(processingSummary.status.label),
                    description: processingSummary.status.description
                        ? t(processingSummary.status.description)
                        : undefined
                } : null}
                toolbar={
                    <Button color="primary" outline type="button">
                        <i className="ri-sparkling-line me-1" />
                        {t("AI Assessment Assistant")}
                    </Button>
                }
                summaries={processingSummary.cards}
                summariesLoading={summaryIsFetching && !complianceModule}
                onSummaryAction={setActiveSummaryCard}
            >
                {filledQuestionnairesAreFetching || processingQuestionsQuery.isFetching ? (
                    <div className="p-3">
                        {[1, 2, 3].map((item) => (
                            <p
                                key={item}
                                style={{ height: "55px" }}
                                className="w-100 dt-field dt-skeleton"
                            />
                        ))}
                    </div>
                ) : (
                    <div className="assessment-panel__workspace smartintegrity__questionnaire__tabs_progress">
                        <aside className="assessment-panel__sidebar">
                            <div className="assessment-panel__sidebar-heading">
                                <strong>{t("Processing Activity Sections")}</strong>
                                <Button
                                    color="primary"
                                    outline
                                    size="sm"
                                    onClick={() => setShowCreationModal(true)}
                                    disabled={
                                        !softwareDetails ||
                                        filledQuestionnairesAreFetching
                                    }
                                >
                                    <i className="ri-add-line me-1" />
                                    {t("Add")}
                                </Button>
                            </div>

                            <div className="assessment-panel__list">
                                {(filledQuestionnaires || []).map((report, index) => {
                                    const progress =
                                        activityProgress[report.case_id] ||
                                        getReportProgress(report);
                                    const gapCount = getReportGapCount(
                                        report,
                                        obligationsByCode
                                    );
                                    const isSelected = reportCaseIdsEqual(
                                        report.case_id,
                                        selectedActivityId
                                    );

                                    return (
                                        <div
                                            key={report.case_id}
                                            role="button"
                                            tabIndex={0}
                                            className={`assessment-panel__item ${
                                                isSelected ? "active" : ""
                                            }`}
                                            onClick={() =>
                                                handleSelectActivity(report.case_id)
                                            }
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    handleSelectActivity(
                                                        report.case_id
                                                    );
                                                }
                                            }}
                                        >
                                            <span className="assessment-panel__number">
                                                {index + 1}
                                            </span>
                                            <span className="assessment-panel__item-main">
                                                <strong>{report.module_name}</strong>
                                                <small>
                                                    {`${progress.percent}% · ${progress.answered}/${progress.total} ${t("answered")}`}
                                                </small>
                                            </span>
                                            <span className="assessment-panel__item-meta">
                                                <span
                                                    className={`assessment-panel__status ${
                                                        gapCount
                                                            ? "gap"
                                                            : progress.percent >= 100
                                                            ? "completed"
                                                            : "in-progress"
                                                    }`}
                                                >
                                                    {gapCount
                                                        ? `${gapCount} ${t("Documentation Gap")}`
                                                        : progress.percent >= 100
                                                        ? t("Completed")
                                                        : t("In Progress")}
                                                </span>
                                            </span>

                                            <UncontrolledDropdown
                                                className="assessment-panel__menu"
                                                onClick={(event) =>
                                                    event.stopPropagation()
                                                }
                                            >
                                                <DropdownToggle
                                                    tag="button"
                                                    type="button"
                                                    className="assessment-panel__menu-toggle"
                                                    aria-label={t("Activity actions")}
                                                >
                                                    <i className="ri-more-2-fill" />
                                                </DropdownToggle>
                                                <DropdownMenu right>
                                                    <DropdownItem
                                                        onClick={() =>
                                                            setActivityToEdit(report)
                                                        }
                                                    >
                                                        <i className="ri-pencil-line me-2" />
                                                        {t("Edit")}
                                                    </DropdownItem>
                                                    <DropdownItem
                                                        className="text-danger"
                                                        onClick={() =>
                                                            setActivityToDelete(
                                                                report.case_id
                                                            )
                                                        }
                                                    >
                                                        <i className="ri-delete-bin-line me-2" />
                                                        {t("Delete")}
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </UncontrolledDropdown>
                                        </div>
                                    );
                                })}

                                {!filledQuestionnaires?.length && (
                                    <div className="text-muted small p-3">
                                        {t("No processing activities have been added.")}
                                    </div>
                                )}
                            </div>
                        </aside>

                        <main className="assessment-panel__content">
                            {selectedReport ? (
                                <ProcessingQuestionnaire
                                    key={selectedReport.case_id}
                                    report={selectedReport}
                                    index={(filledQuestionnaires || []).findIndex(
                                        (report) =>
                                            report.case_id === selectedReport.case_id
                                    )}
                                    softwareId={softwareId}
                                    complianceObligations={complianceObligations}
                                    questionFocus={questionFocusForSelectedReport}
                                    onSaved={handleActivitySaved}
                                    onProgressChange={handleProgressChange}
                                />
                            ) : (
                                <div className="assessment-panel__empty">
                                    {t(
                                        "Select a processing activity to view its questionnaire."
                                    )}
                                </div>
                            )}
                        </main>
                    </div>
                )}
            </AssessmentPanel>

            <AssessmentSummaryOffcanvas
                isOpen={!!activeSummaryCard}
                details={activeSummaryCard?.details}
                onClose={() => setActiveSummaryCard(null)}
            />

			<span ref={pageEndRef} />

			<Modal
				isOpen={activityToDelete}
				toggle={toggleActivityDeletionModal}
			>
				<ModalHeader
					toggle={toggleActivityDeletionModal}
				>
					{t("Delete Activity")}
				</ModalHeader>
				<ModalBody>
					<p>
						{t("Are you sure?")}
					</p>
				</ModalBody>
				<ModalFooter>
					<Button
						color="secondary"
						onClick={toggleActivityDeletionModal}
						className="me-2"
					>
						{t("Cancel")}
					</Button>

					<Button
						color="danger"
						onClick={() => deleteActivity({
							caseId: activityToDelete
						})}
						disabled={activityDeletionIsLoading}
					>
						{activityDeletionIsLoading && (
							<Spinner size='sm' />
						)}
						{t("Delete")}
					</Button>
				</ModalFooter>
			</Modal>

			{softwareDetails && (
                <>
                    <CreationModal 
                        toggleModal={toggleShowCreationModal}
                        modalIsOpen={showCreationModal}
                        onCreationSuccess={() => {
							handleActivitySaved();

							setTimeout(() => {
								if (pageEndRef.current) {
									pageEndRef.current.scrollIntoView({
										behavior: 'smooth',
										block: 'center',
										inline: 'nearest'
									})
								}
							}, 500);
						}}
                        software={softwareDetails}
                    />
                    <EditModal 
                        toggleModal={toggleActivityEditModal}
                        modalIsOpen={!!activityToEdit}
                        oldData={activityToEdit}
                        onEditSuccess={handleActivitySaved}
                        software={softwareDetails}
                    />
                </>
			)}
		</>
	);
};

const mapStatetoProps = (state) => {
	const { token, user } = state.Login;
	const { Organization } = state;

	return {
		token,
		user,
		organization: Organization,
	};
};

export default withNamespaces()(
	connect(mapStatetoProps, {
		errorNotification,
		successNotification,
	})(ProcessingActivity)
);
